import { useMemo } from 'react';

import {
  BaseCell,
  BaseHeader,
  DataTable,
  TableAvatar,
  TableHeaderFilter,
} from '@crehana/compass.ui/table';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type PaginationState,
} from '@crehana/compass.ui/table/tanstack';

import { useHomeTranslations } from '../../../../hooks/use-home-translations';
import type { THolidayDetail } from '../../mocks';
import type { useGetHolidayDetails } from './use-get-holiday-details';

const columnHelper = createColumnHelper<THolidayDetail['users'][number]>();

const buildColumns = ({
  columnFilters,
  t,
}: {
  columnFilters?: ColumnFiltersState;
  t: ReturnType<typeof useHomeTranslations>['t'];
}) => [
  columnHelper.accessor('profile', {
    id: 'profile',
    header: t('HOLIDAY_DETAIL_TABLE_COLUMN_NAME'),
    footer: info => info.column.id,
    cell: info => {
      const { name, profilePicture, email } = info.getValue();

      return (
        <BaseCell>
          <TableAvatar src={profilePicture.url} />
          <span className="tw-flex tw-flex-col tw-gap-0.2 tw-min-w-0 tw-flex-1">
            <span className="tw-font-link1">{name}</span>
            <span className="tw-truncate tw-text-neutral-light-700 dark:tw-text-neutral-dark-400">
              {email}
            </span>
          </span>
        </BaseCell>
      );
    },
  }),
  columnHelper.accessor('jobPosition', {
    id: 'jobPosition',
    header: info => {
      return (
        <BaseHeader>
          {t('HOLIDAY_DETAIL_TABLE_COLUMN_JOB_CATEGORY')}
          <TableHeaderFilter
            id={info.header.id}
            options={Array.from({ length: 8 }).map((_, index) => ({
              value: index.toString(),
              label: `Policy ${index + 1}`,
            }))}
            columnFilters={columnFilters}
            onFilterChange={info.table.setColumnFilters}
            removePortal
          />
        </BaseHeader>
      );
    },
    footer: info => info.column.id,
    cell: info => {
      const { name } = info.getValue();

      return (
        <span className="tw-font-body5 tw-text-neutral-light-700">{name}</span>
      );
    },
  }),
  columnHelper.accessor('area', {
    id: 'area',
    header: () => {
      return <BaseHeader>{t('HOLIDAY_DETAIL_TABLE_COLUMN_AREA')}</BaseHeader>;
    },
    footer: info => info.column.id,
    cell: info => {
      const { name } = info.getValue();
      return (
        <span className="tw-font-body5 tw-text-neutral-light-700">{name}</span>
      );
    },
  }),
  columnHelper.accessor('country', {
    id: 'country',
    header: info => {
      return (
        <BaseHeader>
          {t('HOLIDAY_DETAIL_TABLE_COLUMN_COUNTRY')}
          <TableHeaderFilter
            id={info.header.id}
            options={Array.from({ length: 8 }).map((_, index) => ({
              value: index.toString(),
              label: `Country ${index + 1}`,
            }))}
            columnFilters={columnFilters}
            onFilterChange={info.table.setColumnFilters}
            removePortal
          />
        </BaseHeader>
      );
    },
    footer: info => info.column.id,
    cell: info => {
      const { name } = info.getValue();

      return (
        <span className="tw-font-body5 tw-text-neutral-light-700">{name}</span>
      );
    },
  }),
];

const useColumns = (props: {
  columnFilters?: ColumnFiltersState;
  t: ReturnType<typeof useHomeTranslations>['t'];
}) => {
  const columns = useMemo(() => buildColumns(props), [props]);

  return columns;
};

type THolidayDetailTableProps = {
  data?: THolidayDetail | null;
  pagination: PaginationState;
  search: string;
  columnFilters: ColumnFiltersState;
  onPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  onSearchChange: React.Dispatch<React.SetStateAction<string>>;
  onColumnFiltersChange: React.Dispatch<
    React.SetStateAction<ColumnFiltersState>
  >;
  loading: boolean;
  error?: ReturnType<typeof useGetHolidayDetails>['error'];
  refetch: () => void;
};

export const HolidayDetailTable = ({
  data,
  pagination,
  onPagination,
  search,
  onSearchChange,
  columnFilters,
  onColumnFiltersChange,
  loading,
  error,
  refetch,
}: THolidayDetailTableProps) => {
  const { t } = useHomeTranslations();

  const columns = useColumns({
    columnFilters,
    t,
  });

  const users = data?.users ?? [];

  return (
    <>
      <DataTable
        loading={loading}
        error={!!error}
        refetch={refetch}
        tanstackProps={{
          data: users,
          columns,
          rowCount: data?.usersCount ?? 0,
          onPaginationChange: onPagination,
          onSearchChange: onSearchChange,
          onColumnFiltersChange: onColumnFiltersChange,
          state: {
            pagination,
            columnFilters,
            search,
          },
        }}
        tableTitle={t('USERS')}
        searchProps={{
          placeholder: t('SEARCH_USER_INPUT_PLACEHOLDER'),
        }}
        columnGrowId="profile"
      />
    </>
  );
};
